import React, { useEffect, useState,  } from "react";
import { Form, Spin, Tabs, Button, message, Switch } from "antd";
import {
  ArrowLeftOutlined,
  SaveOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import { useParams, useNavigate } from "react-router-dom";

import useProject from "../../../hooks/projects/useProject";
import useProjectTracking from "../../../hooks/projects/useProjectTracking";
import { updateProject, updateTracking } from "../../../api/projectApi";
import { flattenTracking, cleanTrackingPayload } from "../../../utils/trackingUtils";

import CommonDataForm from "../../../components/SchemeForm/CommonDataForm";
import GovtLandForm from "../../../components/SchemeForm/GovtLandForm";
import MidcLandForm from "../../../components/SchemeForm/MidcLandForm";
import PrivateLandForm from "../../../components/SchemeForm/PrivateLandForm";

import "./SchemeForm.css";

const LAND_TYPE_STEPS = {
  GOVT: [
    { key: "govt_1", label: "Land Bank" },
    { key: "govt_2", label: "Proposal Routing" },
    { key: "govt_3", label: "NOC & Clearances" },
    { key: "govt_4", label: "Joint Measurement" },
    { key: "govt_5", label: "Possession" },
  ],
  MIDC: [
    { key: "midc_1", label: "Land Demand" },
    { key: "midc_2", label: "Land Identification" },
    { key: "midc_3", label: "Proposal Submission" },
  ],
  PRIVATE: [
    { key: "private_1", label: "Land Demand" },
    { key: "private_2", label: "Land Initiation" },
    { key: "private_3", label: "Land Selection" },
    { key: "private_4", label: "Land Verification" },
  ],
};

const LAND_TYPE_COMPONENT = {
  GOVT: GovtLandForm,
  MIDC: MidcLandForm,
  PRIVATE: PrivateLandForm,
};

const LAND_TYPE_BADGE = {
  GOVT: { label: "Government Land", color: "#0ea5e9" },
  MIDC: { label: "MIDC Land", color: "#8b5cf6" },
  PRIVATE: { label: "Private Land", color: "#f59e0b" },
};

function SchemeForm() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("project");
  const [autoSave, setAutoSave] = useState(true);

  const [projectForm] = Form.useForm();
  const [trackingForm] = Form.useForm();

  const { project, loading, fetchProject } = useProject();
  const { tracking, loading: trackingLoading, fetchTracking } = useProjectTracking();

  useEffect(() => {
    if (!id) return;
    fetchProject(id);
    fetchTracking(id);
  }, [id, fetchProject, fetchTracking]);

  useEffect(() => {
    if (!tracking) return;
    const values = flattenTracking(tracking);



trackingForm.setFieldsValue(values);
    trackingForm.setFieldsValue(flattenTracking(tracking));
  }, [tracking, trackingForm]);

  const handleSave = async (isFinal = false) => {
    let projectValues;
    try {
      projectValues = await projectForm.validateFields();
    } catch {
      message.error("Please fix the highlighted errors in Project Info");
      return;
    }

    setSaving(true);
    try {
      await updateProject(id, projectValues);
      const liveTrackingValues = trackingForm.getFieldsValue(true);
      const mergedTracking = {
        ...flattenTracking(tracking),
        ...liveTrackingValues,
      };
      await updateTracking(id, cleanTrackingPayload(mergedTracking));

      if (isFinal) {
        navigate("/schemes");
      } else {
        // refresh our "last known good" snapshot so the next merge is accurate
        fetchTracking(id);
      }
    } catch (err) {
    
    } finally {
      setSaving(false);
    }
  };

  const handleTabChange = (key) => {
    setActiveTab(key);
    if (autoSave) {
      handleSave(false);
    }
  };

  // -------------------------------------------------------------------------
  // Tabs
  // -------------------------------------------------------------------------
  const LandFormComponent = LAND_TYPE_COMPONENT[project?.land_type];
  const landSteps = LAND_TYPE_STEPS[project?.land_type] || [];

  const tabs = [
    {
      label: "Project Info",
      key: "project",
      children: <CommonDataForm form={projectForm} project={project} />,
    },
    ...(LandFormComponent
      ? landSteps.map(({ key, label }) => ({
          label,
          key,
          children: (
            <LandFormComponent
              form={trackingForm}
              tracking={tracking}
              stepKey={key}
            />
          ),
        }))
      : []),
  ];

  if (loading || trackingLoading) {
    return (
      <div className="scheme-form-loading">
        <Spin size="large" />
        <p>Loading project details…</p>
      </div>
    );
  }

  const landTypeBadge = LAND_TYPE_BADGE[project?.land_type] ?? {
    label: project?.land_type,
    color: "#6b7280",
  };

  return (
    <div className="scheme-form-container">
      {/* Header */}
      <div className="scheme-form-header">
        <button className="back-btn" onClick={() => navigate("/schemes")}>
          <ArrowLeftOutlined />
        </button>

        <div className="header-meta">
          <div className="header-top-row">
            <h1 className="header-title">{project?.name}</h1>

            <span
              className="land-type-badge"
              style={{
                background: landTypeBadge.color + "18",
                color: landTypeBadge.color,
                borderColor: landTypeBadge.color + "40",
              }}
            >
              {landTypeBadge.label}
            </span>
          </div>

          <p className="header-subtitle">
            {[project?.district, project?.taluka, project?.village]
              .filter(Boolean)
              .join(" · ")}
          </p>
        </div>
      </div>

      {/* Progress */}
      {tracking?.total_project_progress != null && (
        <div className="progress-bar-wrapper">
          <div className="progress-bar-inner">
            <span className="progress-label">Overall Progress</span>
            <span className="progress-pct">
              {tracking.total_project_progress}%
            </span>
          </div>

          <div className="progress-track">
            <div
              className="progress-fill"
              style={{ width: `${tracking.total_project_progress}%` }}
            />
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="scheme-form-tabs">
        <Tabs
          activeKey={activeTab}
          onChange={handleTabChange}
          items={tabs}
          tabPosition="top"
          size="large"
          className="custom-tabs"
        />
      </div>

      {/* Footer */}
      <div className="scheme-form-footer">
        <label className="auto-save-toggle">
          <Switch checked={autoSave} onChange={setAutoSave} size="small" />
          <span>Auto-save on tab switch</span>
        </label>

        <div className="footer-actions">
          <Button
            className="btn-save"
            loading={saving}
            icon={<SaveOutlined />}
            onClick={() => handleSave(false)}
          >
            Save Progress
          </Button>

          <Button
            type="primary"
            className="btn-finish"
            loading={saving}
            icon={<CheckCircleOutlined />}
            onClick={() => handleSave(true)}
          >
            Save & Finish
          </Button>
        </div>
      </div>
    </div>
  );
}

export default SchemeForm;